// src/pages/auth/SecuritySetup.js - Security Setup Flow Component
import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  InputAdornment,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Security as SecurityIcon,
  Phone as PhoneIcon,
  VpnLock as VpnIcon,
  Key as KeyIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckIcon,
  Settings as SettingsIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

const SecuritySetup = () => {
  const {
    setup2FA,
    verify2FASetup,
    setup2FAWithToken,
    verify2FASetupWithToken,
    setupVPNAccess,
    loading,
    error,
    clearError,
    loginGuidance,
    getSetupRequirements,
    isSuperAdmin,
  } = useAuth();

  const location = useLocation();
  // setup_token is passed from the login page when the user needs to enroll in 2FA
  const setupToken = location.state?.setupToken;

  // Local state for component-specific errors
  const [localError, setLocalError] = useState("");

  const navigate = useNavigate();

  // Step management
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [setupData, setSetupData] = useState({
    phoneNumber: "",
    countryCode: "+1",
    verificationCode: "",
    allowedIpRanges: [""],
    apiKey: "",
  });

  // UI states
  const [showApiKey, setShowApiKey] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [smsCodeSent, setSmsCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Setup requirements from auth context
  const requirements = getSetupRequirements();
  const steps = [];

  // Build steps based on requirements
  if (requirements.includes("2fa")) {
    steps.push("Two-Factor Authentication");
  }
  if (requirements.includes("vpn_access") || requirements.includes("api_key")) {
    steps.push("VPN & API Key Setup");
  }
  steps.push("Complete");

  useEffect(() => {
    clearError();

    // If no requirements, redirect back
    if (requirements.length === 0) {
      navigate("/login");
    }
  }, [requirements, navigate, clearError]);

  // Countdown timer for SMS resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleInputChange = (field, value) => {
    setSetupData((prev) => ({ ...prev, [field]: value }));
    clearError();
    setLocalError(""); // Clear local error as well
  };

  const handleArrayInputChange = (index, value) => {
    const newRanges = [...setupData.allowedIpRanges];
    newRanges[index] = value;
    setSetupData((prev) => ({ ...prev, allowedIpRanges: newRanges }));
  };

  const addIpRange = () => {
    setSetupData((prev) => ({
      ...prev,
      allowedIpRanges: [...prev.allowedIpRanges, ""],
    }));
  };

  const removeIpRange = (index) => {
    if (setupData.allowedIpRanges.length > 1) {
      const newRanges = setupData.allowedIpRanges.filter((_, i) => i !== index);
      setSetupData((prev) => ({ ...prev, allowedIpRanges: newRanges }));
    }
  };

  // 2FA Setup - uses setup token when available (mandatory 2FA enrollment),
  // otherwise falls back to regular authenticated endpoint
  const handle2FASetup = async () => {
    clearError();
    setLocalError("");

    try {
      let result;
      if (setupToken) {
        result = await setup2FAWithToken(
          setupToken,
          setupData.phoneNumber,
          setupData.countryCode,
          setupData.verificationCode
        );
      } else {
        result = await setup2FA(
          setupData.phoneNumber,
          setupData.countryCode,
          setupData.verificationCode
        );
      }

      if (result.success && !result.requires_verification) {
        // Setup completed immediately
        setBackupCodes(result.backup_codes || []);
        setCompletedSteps((prev) => new Set([...prev, currentStep]));
        setShowBackupDialog(true);
      } else if (result.requires_verification) {
        // SMS sent, need verification
        setSmsCodeSent(true);
        setCountdown(60);
      }
    } catch (err) {
      console.error("2FA setup error:", err);
      setLocalError(err.message || "Failed to setup 2FA");
    }
  };

  const handleVerify2FA = async () => {
    clearError();
    setLocalError("");

    try {
      let result;
      if (setupToken) {
        result = await verify2FASetupWithToken(
          setupToken,
          setupData.verificationCode
        );
      } else {
        result = await verify2FASetup(setupData.verificationCode);
      }

      if (result.success) {
        setBackupCodes(result.backup_codes || []);
        setCompletedSteps((prev) => new Set([...prev, currentStep]));
        setShowBackupDialog(true);
      }
    } catch (err) {
      console.error("2FA verification error:", err);
      setLocalError(err.message || "Failed to verify 2FA");
    }
  };

  // VPN Setup
  const handleVPNSetup = async () => {
    clearError();
    setLocalError("");

    try {
      // Filter out empty IP ranges
      const validRanges = setupData.allowedIpRanges.filter(
        (range) => range.trim() !== ""
      );

      if (validRanges.length === 0) {
        setLocalError("Please enter at least one IP range");
        return;
      }

      const result = await setupVPNAccess(validRanges);

      if (result.success) {
        setSetupData((prev) => ({ ...prev, apiKey: result.api_key }));
        setCompletedSteps((prev) => new Set([...prev, currentStep]));
        setShowApiKey(true);
      }
    } catch (err) {
      console.error("VPN setup error:", err);
      setLocalError(err.message || "Failed to setup VPN access");
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    navigate("/login");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const downloadBackupCodes = () => {
    const content = `MedFlowHub Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\n\n${backupCodes.join(
      "\n"
    )}\n\nKeep these codes in a secure location.`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "medflowhub-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const isCurrentStepValid = () => {
    const stepType = steps[currentStep];

    if (stepType === "Two-Factor Authentication") {
      if (!smsCodeSent) {
        return setupData.phoneNumber.length >= 10;
      } else {
        return setupData.verificationCode.length === 6;
      }
    }

    if (stepType === "VPN & API Key Setup") {
      return setupData.allowedIpRanges.some((range) => range.trim() !== "");
    }

    return true;
  };

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 4,
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4, width: "100%" }}>
          <SettingsIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
          <Typography variant="h3" sx={{ mb: 1, fontWeight: 700 }}>
            Security Setup
          </Typography>
          <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
            Complete your security configuration
          </Typography>

          {loginGuidance?.message && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {loginGuidance.message}
            </Alert>
          )}
        </Box>

        {/* Main Setup Card */}
        <Paper
          elevation={8}
          sx={{ width: "100%", borderRadius: 3, overflow: "hidden" }}
        >
          {/* Progress Stepper */}
          <Box sx={{ p: 3, pb: 1, bgcolor: "primary.main", color: "white" }}>
            <Stepper
              activeStep={currentStep}
              alternativeLabel
              sx={{
                "& .MuiStepLabel-root .Mui-completed": { color: "white" },
                "& .MuiStepLabel-root .Mui-active": { color: "white" },
                "& .MuiStepLabel-label": { color: "rgba(255, 255, 255, 0.7)" },
                "& .MuiStepLabel-label.Mui-active": { color: "white" },
                "& .MuiStepLabel-label.Mui-completed": { color: "white" },
              }}
            >
              {steps.map((label) => (
                <Step
                  key={label}
                  completed={completedSteps.has(steps.indexOf(label))}
                >
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Box sx={{ p: 4 }}>
            {/* Loading indicator */}
            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            {/* Error Display */}
            {(error || localError) && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error || localError}
              </Alert>
            )}

            {/* Step: Two-Factor Authentication */}
            {steps[currentStep] === "Two-Factor Authentication" && (
              <Box>
                <Card
                  variant="outlined"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: "warning.light",
                    bgcolor: "warning.50",
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <PhoneIcon color="warning" sx={{ mr: 1 }} />
                      <Typography variant="h6" color="warning.main">
                        Setup Two-Factor Authentication
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      SMS-based 2FA is required for HIPAA compliance. Enter your
                      phone number to get started.
                    </Typography>
                  </CardContent>
                </Card>

                {!smsCodeSent ? (
                  <Box>
                    <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                      <TextField
                        select
                        label="Country"
                        value={setupData.countryCode}
                        onChange={(e) =>
                          handleInputChange("countryCode", e.target.value)
                        }
                        sx={{ width: "120px" }}
                        disabled={loading}
                        SelectProps={{ native: true }}
                      >
                        <option value="+1">+1 (US)</option>
                        <option value="+44">+44 (UK)</option>
                        <option value="+33">+33 (FR)</option>
                        <option value="+49">+49 (DE)</option>
                        <option value="+81">+81 (JP)</option>
                      </TextField>

                      <TextField
                        fullWidth
                        label="Phone Number"
                        value={setupData.phoneNumber}
                        onChange={(e) =>
                          handleInputChange("phoneNumber", e.target.value)
                        }
                        required
                        disabled={loading}
                        helperText="Enter your phone number for SMS verification"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handle2FASetup}
                      disabled={loading || !isCurrentStepValid()}
                      sx={{
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        borderRadius: 2,
                      }}
                      startIcon={<PhoneIcon />}
                    >
                      {loading ? "Sending SMS..." : "Send Verification Code"}
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Alert severity="info" sx={{ mb: 3 }}>
                      SMS verification code sent to {setupData.countryCode}{" "}
                      {setupData.phoneNumber}
                    </Alert>

                    <TextField
                      fullWidth
                      label="SMS Verification Code"
                      value={setupData.verificationCode}
                      onChange={(e) =>
                        handleInputChange(
                          "verificationCode",
                          e.target.value.replace(/\D/g, "")
                        )
                      }
                      disabled={loading}
                      inputProps={{ maxLength: 6 }}
                      placeholder="123456"
                      helperText={`${setupData.verificationCode.length}/6 digits entered`}
                      sx={{ mb: 3 }}
                      InputProps={{
                        style: {
                          textAlign: "center",
                          fontSize: "1.2rem",
                          letterSpacing: "0.3rem",
                        },
                      }}
                    />

                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSmsCodeSent(false);
                          setSetupData((prev) => ({
                            ...prev,
                            verificationCode: "",
                          }));
                          setCountdown(0);
                        }}
                        disabled={loading}
                        sx={{ flex: 1 }}
                      >
                        Change Phone Number
                      </Button>

                      <Button
                        variant="contained"
                        onClick={handleVerify2FA}
                        disabled={loading || !isCurrentStepValid()}
                        sx={{
                          flex: 2,
                          py: 1.5,
                          fontSize: "1rem",
                          fontWeight: 600,
                          borderRadius: 2,
                        }}
                        startIcon={<CheckIcon />}
                      >
                        {loading ? "Verifying..." : "Verify Code"}
                      </Button>
                    </Box>

                    {countdown === 0 && (
                      <Button
                        fullWidth
                        variant="text"
                        onClick={handle2FASetup}
                        disabled={loading}
                        sx={{ mt: 2 }}
                      >
                        Resend SMS Code
                      </Button>
                    )}

                    {countdown > 0 && (
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{ mt: 2, color: "text.secondary" }}
                      >
                        Resend available in {countdown} seconds
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Step: VPN & API Key Setup */}
            {steps[currentStep] === "VPN & API Key Setup" && (
              <Box>
                <Card
                  variant="outlined"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: "info.light",
                    bgcolor: "info.50",
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <VpnIcon color="info" sx={{ mr: 1 }} />
                      <Typography variant="h6" color="info.main">
                        VPN & API Key Configuration
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Configure allowed IP ranges for VPN access and generate a
                      personal API key for secure authentication.
                    </Typography>
                  </CardContent>
                </Card>

                <Typography variant="h6" sx={{ mb: 2 }}>
                  Allowed IP Ranges
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 3 }}
                >
                  Enter the IP addresses or ranges that you will connect from.
                  This includes your VPN exit IPs.
                </Typography>

                {setupData.allowedIpRanges.map((range, index) => (
                  <Box key={index} sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <TextField
                      fullWidth
                      label={`IP Range ${index + 1}`}
                      value={range}
                      onChange={(e) =>
                        handleArrayInputChange(index, e.target.value)
                      }
                      placeholder="192.168.1.0/24 or 203.0.113.0/24"
                      disabled={loading}
                      helperText="Enter CIDR notation (e.g., 192.168.1.0/24) or single IP"
                    />
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => removeIpRange(index)}
                      disabled={
                        setupData.allowedIpRanges.length === 1 || loading
                      }
                      sx={{ minWidth: "100px" }}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}

                <Button
                  variant="outlined"
                  onClick={addIpRange}
                  disabled={loading}
                  sx={{ mb: 3, mr: 2 }}
                >
                  Add IP Range
                </Button>

                {completedSteps.has(currentStep) && setupData.apiKey && (
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 3,
                      bgcolor: "success.50",
                      border: "2px solid",
                      borderColor: "success.light",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h6"
                        color="success.main"
                        sx={{ mb: 2, display: "flex", alignItems: "center" }}
                      >
                        <KeyIcon sx={{ mr: 1 }} />
                        Your Personal API Key
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 2 }}
                      >
                        Save this API key securely. You'll need it for all
                        future logins. It will not be shown again.
                      </Typography>
                      <TextField
                        fullWidth
                        value={setupData.apiKey}
                        type={showApiKey ? "text" : "password"}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  copyToClipboard(setupData.apiKey)
                                }
                              >
                                <CopyIcon />
                              </IconButton>
                              <IconButton
                                onClick={() => setShowApiKey(!showApiKey)}
                              >
                                {showApiKey ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 2, fontFamily: "monospace" }}
                      />
                      <Alert severity="warning">
                        <strong>Important:</strong> Store this API key in a
                        secure password manager. You will need it every time you
                        log in.
                      </Alert>
                    </CardContent>
                  </Card>
                )}

                {!completedSteps.has(currentStep) && (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleVPNSetup}
                    disabled={loading || !isCurrentStepValid()}
                    sx={{
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      borderRadius: 2,
                    }}
                    startIcon={<VpnIcon />}
                  >
                    {loading
                      ? "Setting up VPN Access..."
                      : "Generate API Key & Configure VPN"}
                  </Button>
                )}
              </Box>
            )}

            {/* Step: Complete */}
            {steps[currentStep] === "Complete" && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CheckIcon
                  color="success"
                  sx={{ fontSize: 80, mb: 2, opacity: 0.9 }}
                />

                <Typography
                  variant="h5"
                  color="success.main"
                  sx={{ mb: 2, fontWeight: 600 }}
                >
                  Security Setup Complete!
                </Typography>

                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ mb: 3 }}
                >
                  Your account now has maximum security configuration for HIPAA
                  compliance.
                </Typography>

                {/* Security features summary */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 1,
                    mb: 3,
                    flexWrap: "wrap",
                  }}
                >
                  {requirements.includes("2fa") && (
                    <Chip
                      icon={<PhoneIcon />}
                      label="SMS 2FA Enabled"
                      color="success"
                    />
                  )}
                  {(requirements.includes("vpn_access") ||
                    requirements.includes("api_key")) && (
                    <>
                      <Chip
                        icon={<VpnIcon />}
                        label="VPN Required"
                        color="primary"
                      />
                      <Chip
                        icon={<KeyIcon />}
                        label="API Key Generated"
                        color="info"
                      />
                    </>
                  )}
                  <Chip
                    icon={<SecurityIcon />}
                    label="HIPAA Compliant"
                    color="success"
                  />
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleComplete}
                  sx={{
                    mb: 2,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                  startIcon={<SecurityIcon />}
                >
                  Continue to Login
                </Button>

                <Typography variant="body2" color="text.secondary">
                  You can now use the secure login with all your configured
                  security features.
                </Typography>
              </Box>
            )}

            {/* Navigation Buttons */}
            {steps[currentStep] !== "Complete" && (
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/login")}
                  startIcon={<BackIcon />}
                  disabled={loading}
                >
                  Back to Login
                </Button>

                {completedSteps.has(currentStep) &&
                  currentStep < steps.length - 1 && (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={loading}
                    >
                      Next Step
                    </Button>
                  )}
              </Box>
            )}
          </Box>
        </Paper>

        {/* Backup Codes Dialog */}
        <Dialog
          open={showBackupDialog}
          onClose={() => setShowBackupDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <KeyIcon color="warning" />
              Save Your Backup Codes
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <strong>Important:</strong> Save these backup codes in a secure
              location. You can use them to access your account if you lose your
              phone.
            </Alert>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Each code can only be used once. Store them in a secure password
              manager or print them out.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1,
                mb: 3,
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.300",
              }}
            >
              {backupCodes.map((code, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1,
                    bgcolor: "white",
                    borderRadius: 1,
                    fontFamily: "monospace",
                    fontSize: "0.9rem",
                    textAlign: "center",
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  {code}
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Button
                variant="outlined"
                onClick={() => copyToClipboard(backupCodes.join("\n"))}
                startIcon={<CopyIcon />}
                fullWidth
              >
                Copy All Codes
              </Button>
              <Button
                variant="outlined"
                onClick={downloadBackupCodes}
                startIcon={<DownloadIcon />}
                fullWidth
              >
                Download as File
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowBackupDialog(false);
                if (currentStep < steps.length - 1) {
                  handleNext();
                }
              }}
              variant="contained"
            >
              I've Saved My Backup Codes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Help Section */}
        <Paper sx={{ mt: 3, p: 3, width: "100%" }}>
          <Typography
            variant="h6"
            sx={{ mb: 2, display: "flex", alignItems: "center" }}
          >
            <WarningIcon color="info" sx={{ mr: 1 }} />
            Security Requirements
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <PhoneIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="SMS Two-Factor Authentication"
                secondary="Required for all accounts to meet HIPAA compliance standards"
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <VpnIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="VPN Access Control"
                secondary="Restricts access to authorized IP ranges and VPN connections"
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <KeyIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Personal API Key"
                secondary="Unique authentication key required for all secure login attempts"
              />
            </ListItem>
          </List>
        </Paper>
      </Box>
    </Container>
  );
};

export default SecuritySetup;
